/**
 * View-mode → phase mapping for cycle-102 sprint-2 Task 2.4.
 *
 * Per SDD §1.4.2 migration path + R-SDD-9 + Open Q-6, the legacy 6
 * EnvironmentGraphPage view modes (build/monitor/runs/memory + lineage
 * + review) are mapped onto the 4 design-handoff phase tabs, with
 * `lineage` and `review` surviving as URL-only sub-modes inside RECALL
 * until phase semantics arrive in cycle-102.5 sprint-2.
 */

import { DEFAULT_PHASE, PHASE_IDS, type PhaseId } from './PhaseTabs';

export type LegacyViewMode =
  | 'build'
  | 'monitor'
  | 'runs'
  | 'memory'
  | 'lineage'
  | 'review';

export const LEGACY_VIEW_MODES: readonly LegacyViewMode[] = [
  'build',
  'monitor',
  'runs',
  'memory',
  'lineage',
  'review',
];

/**
 * Sub-modes that survive inside RECALL — preserved per SDD R-SDD-9 so
 * existing deep-links keep working until cycle-102.5 retires them.
 */
export const RECALL_SUB_MODES = ['lineage', 'review'] as const;
export type RecallSubMode = (typeof RECALL_SUB_MODES)[number];

/** Map legacy view mode → canonical phase. */
export const PHASE_FROM_VIEW_MODE: Record<LegacyViewMode, PhaseId> = {
  build: 'WEAVE',
  monitor: 'RUN',
  runs: 'ATTEST',
  memory: 'RECALL',
  lineage: 'RECALL',
  review: 'RECALL',
};

/** Map canonical phase → primary view mode (for backwards-compat URLs). */
export const VIEW_MODE_FROM_PHASE: Record<PhaseId, LegacyViewMode> = {
  WEAVE: 'build',
  RUN: 'monitor',
  ATTEST: 'runs',
  RECALL: 'memory',
};

/** Sub-mode for a RECALL view — null for memory, 'lineage'/'review' otherwise. */
export function recallSubModeFor(view: LegacyViewMode): RecallSubMode | null {
  if (view === 'lineage' || view === 'review') return view;
  return null;
}

/**
 * Parse `?phase=...&sub=...` URL params into a phase + sub-mode pair.
 * Falls back to DEFAULT_PHASE for unknown phase values; ignores sub
 * for non-RECALL phases.
 */
export function parsePhaseFromSearch(
  search: string | URLSearchParams,
): { phase: PhaseId; sub: RecallSubMode | null } {
  const params =
    typeof search === 'string'
      ? new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
      : search;
  const phaseRaw = params.get('phase')?.toUpperCase();
  const phase = (PHASE_IDS as readonly string[]).includes(phaseRaw ?? '')
    ? (phaseRaw as PhaseId)
    : DEFAULT_PHASE;
  let sub: RecallSubMode | null = null;
  if (phase === 'RECALL') {
    const subRaw = params.get('sub');
    if (subRaw === 'lineage' || subRaw === 'review') {
      sub = subRaw;
    }
  }
  return { phase, sub };
}

/**
 * Serialize a phase + sub-mode pair into URL search params, preserving
 * any existing params other than `phase` and `sub`.
 */
export function serializePhaseToSearch(
  current: string | URLSearchParams,
  next: { phase: PhaseId; sub?: RecallSubMode | null },
): URLSearchParams {
  const params =
    typeof current === 'string'
      ? new URLSearchParams(current.startsWith('?') ? current.slice(1) : current)
      : new URLSearchParams(current);
  params.set('phase', next.phase);
  if (next.phase === 'RECALL' && next.sub) {
    params.set('sub', next.sub);
  } else {
    params.delete('sub');
  }
  return params;
}
